import { SeoHead } from "@/components/SeoHead"
import { ImpersonationBanner } from "@/components/ImpersonationBanner"
import { ReportExitHeader } from "./components/ReportExitHeader"
import { router } from "@inertiajs/react"
import { LifeWheelRadar } from "@/components/planning/LifeWheelRadar"
import { AUDIT_CATEGORIES } from "@/components/planning/audit-questions"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Sparkles, ShieldAlert, FileDown, RefreshCw } from "lucide-react"
import ReactMarkdown from "react-markdown"
// Vite asset import resolves to a URL string — SSR-safe (Report/** is in the
// SSR bundle). The "Kingdom Factor Operating System" infographic (task #181).
import kfOperatingSystem from "@/assets/graphics/kf-operating-system.png"

// Which prose sections render, in what order, with what headings, comes from
// the server (`sectionOrder` / `reportShape` props — IntakeReport#section_order
// and #report_shape), the same seam the PDF renders from. This file holds no
// copy of that list.

// One prose section: optional heading + markdown body. Skips when empty.
function ReportSection({ title, section }) {
  if (!section?.body_markdown) return null
  return (
    <section className="mb-10">
      {title && (
        <h2 className="font-display text-2xl text-zinc-900 mb-4">{title}</h2>
      )}
      <MarkdownProse markdown={section.body_markdown} />
    </section>
  )
}

// Per-area callouts: label + score + progress bar + the AI's 1-2 sentence note.
// The visual bars break up an otherwise text-heavy report.
function AreaCallouts({ areaNotes, wheelTotals }) {
  if (!areaNotes || Object.keys(areaNotes).length === 0) return null
  return (
    <section className="mb-10">
      <h2 className="font-display text-2xl text-zinc-900 mb-5">
        Your Integrated Life Audit Results
      </h2>
      <div className="space-y-5">
        {AUDIT_CATEGORIES.map(({ key, label }) => {
          const note = areaNotes[key]
          if (!note) return null
          const score = wheelTotals?.[key] ?? 0
          const pct = Math.max(0, Math.min(100, Math.round((score / 25) * 100)))
          return (
            <div key={key}>
              <div className="flex items-baseline justify-between mb-1">
                <span className="font-medium text-zinc-900">{label}</span>
                <span className="text-sm text-zinc-500">{score}/25</span>
              </div>
              <div className="h-2 bg-zinc-100 rounded-full overflow-hidden mb-2">
                <div className="h-full bg-kf-red rounded-full" style={{ width: `${pct}%` }} />
              </div>
              <p className="text-sm text-zinc-700 leading-relaxed">{note}</p>
            </div>
          )
        })}
      </div>
    </section>
  )
}

// AI sections come back as real markdown — headings, **bold**, *italic*,
// occasional bullet lists. Render with react-markdown + tight tailwind
// overrides so the prose matches the report's typography. Same pattern as
// MarkdownBubble in GuidedSectionChat. Headings are downgraded one level
// because the section title above already occupies h2.
function MarkdownProse({ markdown }) {
  if (!markdown) return null
  return (
    <ReactMarkdown
      components={{
        p: ({ node, ...props }) => (
          <p
            className="text-zinc-800 leading-relaxed mb-4 last:mb-0"
            {...props}
          />
        ),
        strong: ({ node, ...props }) => (
          <strong className="font-semibold text-zinc-900" {...props} />
        ),
        em: ({ node, ...props }) => <em className="italic" {...props} />,
        h1: ({ node, ...props }) => (
          <h3
            className="font-display text-xl text-zinc-900 mt-6 mb-3"
            {...props}
          />
        ),
        h2: ({ node, ...props }) => (
          <h3
            className="font-display text-xl text-zinc-900 mt-6 mb-3"
            {...props}
          />
        ),
        h3: ({ node, ...props }) => (
          <h4
            className="font-semibold text-base text-zinc-900 mt-4 mb-2"
            {...props}
          />
        ),
        ul: ({ node, ...props }) => (
          <ul
            className="list-disc pl-6 mb-4 space-y-1.5 text-zinc-800 leading-relaxed marker:text-zinc-400"
            {...props}
          />
        ),
        ol: ({ node, ...props }) => (
          <ol
            className="list-decimal pl-6 mb-4 space-y-1.5 text-zinc-800 leading-relaxed marker:text-zinc-500"
            {...props}
          />
        ),
        li: ({ node, ...props }) => <li className="pl-1" {...props} />,
        blockquote: ({ node, ...props }) => (
          <blockquote
            className="border-l-2 border-kf-red/40 pl-4 italic text-zinc-700 my-4"
            {...props}
          />
        ),
        a: ({ node, ...props }) => (
          // eslint-disable-next-line react/jsx-no-target-blank
          <a
            className="underline text-kf-red hover:text-kf-red/80"
            target="_blank"
            rel="noreferrer noopener"
            {...props}
          />
        ),
      }}
    >
      {markdown}
    </ReactMarkdown>
  )
}

// V2 "Personal-First" components. Each numbered component pairs the AI's
// observation callout (built from the prospect's own answers) with the fixed
// client copy (definition + value point) and the fixed "KF Member Examples",
// plus any conservatively-matched member example placed in this component.
function PeerGroupComponents({ componentContent, callouts, matchedExamples }) {
  if (!componentContent?.length) return null

  return (
    <div className="space-y-12 mb-10">
      {componentContent.map((c) => {
        const callout = callouts?.[c.key]
        const matches = (matchedExamples || []).filter((m) => m.section_key === c.key)
        return (
          <section key={c.key}>
            <h2 className="font-display text-2xl text-zinc-900 mb-4">
              <span className="text-kf-red">{c.number}.</span> {c.title}
            </h2>

            {/* Personalized application — the AI observation from the prospect's
                own answers, plus any matched member example for this component. */}
            {(callout || matches.length > 0) && (
              <div className="rounded-lg border-l-4 border-kf-red bg-kf-red/5 p-5 mb-5">
                <p className="text-xs uppercase tracking-[0.15em] text-kf-red font-semibold mb-2">
                  Your Personal Application
                </p>
                {callout && (
                  <p className="text-zinc-800 leading-relaxed">{callout}</p>
                )}
                {matches.map((m, i) => (
                  <p key={i} className="text-sm text-zinc-600 italic mt-3">
                    A Kingdom Factor member faced something similar:{" "}
                    {m.url ? (
                      // eslint-disable-next-line react/jsx-no-target-blank
                      <a
                        href={m.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="not-italic underline text-kf-red hover:text-kf-red/80"
                      >
                        {m.title}
                      </a>
                    ) : (
                      <>&ldquo;{m.title}.&rdquo;</>
                    )}
                  </p>
                ))}
              </div>
            )}

            {/* About This Component — fixed client copy. */}
            <div className="mb-4">
              <h3 className="font-semibold text-zinc-900 mb-1.5">
                About This Component
              </h3>
              <p className="text-zinc-700 leading-relaxed mb-3">{c.definition}</p>
              {c.value_point && (
                <p className="text-zinc-700 leading-relaxed">{c.value_point}</p>
              )}
            </div>

            {/* KF Member Examples — fixed client copy (never AI-generated). */}
            {c.examples?.length > 0 && (
              <div>
                <h3 className="font-semibold text-zinc-900 mb-2">
                  KF Member Examples
                </h3>
                <ul className="list-disc pl-6 space-y-2 text-zinc-700 leading-relaxed marker:text-kf-red/50">
                  {c.examples.map((ex, i) => (
                    <li key={i}>{ex}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}

// KFOS closing block at the END of the V2 report (client feedback 2026-07-17):
// fixed intro paragraph, the KF Operating System graphic, the four generated
// first-person closing sections, with the static "The KF Collective" copy
// rendered before the closing next_steps. Static copy comes from
// PlanningReportSchema (kfosClosing prop) — same source as the PDF.
function ClosingBlock({ kfosClosing, closingSectionOrder, closingSections }) {
  if (!kfosClosing) return null

  const sectionFor = (key) => closingSections?.[key]

  return (
    <div className="mb-10">
      <p className="text-zinc-800 leading-relaxed mb-6">{kfosClosing.intro}</p>
      <figure className="mb-10">
        <img
          src={kfOperatingSystem}
          alt="The Kingdom Factor Operating System — Peer Group, KF Coach, Best Practices, and KF National Community surrounding the Kingdom Factor model"
          className="w-full h-auto rounded-lg border border-zinc-200"
        />
      </figure>

      {(closingSectionOrder || []).map(({ key, title }) => {
        const body = sectionFor(key)
        return (
          <div key={key}>
            {key === "next_steps" && (
              <section className="mb-10">
                <h2 className="font-display text-2xl text-zinc-900 mb-4">
                  {kfosClosing.collective_title}
                </h2>
                {(kfosClosing.collective_paragraphs || []).map((para, i) => (
                  <p
                    key={i}
                    className="text-zinc-800 leading-relaxed mb-4 last:mb-0"
                  >
                    {para}
                  </p>
                ))}
              </section>
            )}
            {body && (
              <section className="mb-10">
                <h2 className="font-display text-2xl text-zinc-900 mb-4">
                  {title}
                </h2>
                <p className="text-zinc-800 leading-relaxed">{body}</p>
              </section>
            )}
          </div>
        )
      })}
    </div>
  )
}

const PROGRAM_LABELS = {
  crucial_conversations: "Crucial Conversations",
  godly_council: "Godly Council",
  imperatives: "Imperatives",
  group_meetings: "Group Meetings",
}

const PROGRAM_LINKS = {
  crucial_conversations: "/app/member/crucial_conversations",
  godly_council: "/app/member/godly_councils",
  imperatives: "/app/member/imperatives",
  group_meetings: "/app/member",
}

function RecommendedPrograms({ programs, currentUser }) {
  if (!programs?.length) return null
  const isMember = currentUser?.role === "member"

  return (
    <section className="mb-10">
      <h2 className="font-display text-2xl text-zinc-900 mb-4">
        Programs That May Help
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {programs.map((program, idx) => {
          const label = PROGRAM_LABELS[program.program_key] || program.program_key
          const href = isMember ? PROGRAM_LINKS[program.program_key] : null
          const cardClasses =
            "block rounded-lg border border-kf-red/20 bg-kf-red/5 p-5 transition-colors hover:bg-kf-red/10 hover:border-kf-red/40"
          const content = (
            <>
              <div className="font-semibold text-kf-red mb-1.5">{label}</div>
              {program.reason && (
                <p className="text-sm text-zinc-700 leading-relaxed">
                  {program.reason}
                </p>
              )}
            </>
          )

          return href ? (
            <a key={idx} href={href} className={cardClasses}>
              {content}
            </a>
          ) : (
            <div key={idx} className={cardClasses}>
              {content}
            </div>
          )
        })}
      </div>
    </section>
  )
}

export default function ReportShow({
  seo,
  report,
  coach,
  wheelTotals,
  accountName,
  currentUser,
  meetingParts = [],
  componentContent = [],
  aboutResults = null,
  kfosClosing = null,
  closingSectionOrder = [],
  regenerateUrl = null,
  sectionOrder = [],
  reportShape = "planning",
}) {
  const isPeerGroup = report?.mode === "peer_group"
  // reportShape (KTD1): "v2_peer_group" | "nine_part_peer_group" |
  // "legacy_peer_group" | "planning" — detected server-side from the stored
  // sections, the same way the PDF does it.
  const isV2PeerGroup = reportShape === "v2_peer_group"
  // A peer-group report renders `opening` on its own above the per-area
  // callouts, so the shape's prose list is rendered from that point on.
  const peerGroupProse = sectionOrder.filter(({ key }) => key !== "opening")

  return (
    <>
      <SeoHead seo={{ ...seo, noIndex: true }} />

      <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white">
        {/* Exit for every viewer: portal link for coach/admin/member, account
            identity + switch/end for a guest, logo + "Sign in" for the public
            token recipient (who still gets no account data at all). */}
        <ReportExitHeader currentUser={currentUser} />
        <ImpersonationBanner />

        {/* AI disclaimer + privacy banner stack */}
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-xs text-amber-900">
          <Sparkles className="inline w-3.5 h-3.5 mr-1" />
          This report was generated with AI assistance. Insights may not be
          perfectly accurate —{" "}
          {isPeerGroup
            ? "your coach can walk through any of it with you."
            : "your coach will discuss specifics with you at your next meeting."}
        </div>
        <div className="bg-zinc-900 text-white px-4 py-2 text-center text-xs">
          <ShieldAlert className="inline w-3.5 h-3.5 mr-1" />
          Shared via private link — do not post publicly.
        </div>

        <main className="max-w-3xl mx-auto px-6 py-10 sm:py-16">
          <header className="mb-10 text-center">
            <p className="text-sm uppercase tracking-[0.2em] text-kf-red mb-3">
              Kingdom Factor Assessment
            </p>
            <h1 className="font-display text-4xl sm:text-5xl text-zinc-900">
              A personalized report for {accountName}
            </h1>
            {coach?.name && (
              <p className="text-sm text-zinc-500 mt-3">
                Prepared by {coach.name}
              </p>
            )}
            {report?.publicToken && (
              <div className="mt-5 flex items-center justify-center gap-3">
                <a
                  href={`/reports/${report.publicToken}.pdf`}
                  className="inline-flex items-center gap-1.5 text-sm text-zinc-700 hover:text-kf-red border border-zinc-300 hover:border-kf-red rounded-md px-3 py-1.5 transition-colors"
                  title="Download this report as a PDF"
                >
                  <FileDown className="w-4 h-4" />
                  Download PDF
                </a>
                {regenerateUrl && (
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          "Regenerate this report into the current format? The existing version will be replaced.",
                        )
                      ) {
                        router.post(regenerateUrl)
                      }
                    }}
                    className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-kf-red border border-dashed border-zinc-300 hover:border-kf-red rounded-md px-3 py-1.5 transition-colors"
                    title="Admin only: regenerate this report"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Regenerate (admin)
                  </button>
                )}
              </div>
            )}
          </header>

          <Card className="mb-10">
            <CardContent className="p-6 sm:p-8 flex flex-col md:flex-row items-center gap-6">
              <LifeWheelRadar scores={wheelTotals || {}} size={280} />
              <div className="flex-1">
                <h2 className="font-display text-xl mb-3">
                  Integrated Life Audit (ILA)
                </h2>
                <ul className="space-y-1 text-sm">
                  {AUDIT_CATEGORIES.map(({ key, label }) => (
                    <li
                      key={key}
                      className="flex justify-between border-b border-zinc-100 py-1 last:border-0"
                    >
                      <span className="text-zinc-700">{label}</span>
                      <span className="font-medium text-zinc-900">
                        {wheelTotals?.[key] ?? 0}/25
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* About Your Results — fixed intro so the reader understands the
              report interprets their answers (task #181). Peer-group only. */}
          {isPeerGroup && aboutResults && (
            <section className="mb-10 rounded-lg border border-zinc-200 bg-zinc-50 p-6">
              <h2 className="font-display text-2xl text-zinc-900 mb-4">
                {aboutResults.title}
              </h2>
              {aboutResults.paragraphs.map((para, i) => (
                <p
                  key={i}
                  className="text-zinc-700 leading-relaxed mb-4 last:mb-0"
                >
                  {para}
                </p>
              ))}
            </section>
          )}

          {/* The Kingdom Factor Operating System infographic (task #181). For
              V2 reports it moved to the KFOS closing block at the end (client
              feedback 2026-07-17) so About Your Results fits one PDF page;
              legacy peer-group shapes keep it here. */}
          {isPeerGroup && !isV2PeerGroup && (
            <figure className="mb-10">
              <img
                src={kfOperatingSystem}
                alt="The Kingdom Factor Operating System — Peer Group, KF Coach, Best Practices, and KF National Community surrounding the Kingdom Factor model"
                className="w-full h-auto rounded-lg border border-zinc-200"
              />
            </figure>
          )}

          {isPeerGroup ? (
            <>
              <ReportSection title={null} section={report.sections.opening} />
              <AreaCallouts
                areaNotes={report.sections.area_notes}
                wheelTotals={wheelTotals}
              />
              {/* The shape's prose after the opening: the case_intro bridge
                  (untitled for V2, titled for nine-part) or the legacy
                  strategic_decision / the_case / coach_cta order. */}
              {peerGroupProse.map(({ key, title }) => (
                <ReportSection key={key} title={title} section={report.sections[key]} />
              ))}
              {isV2PeerGroup ? (
                // V2 "Personal-First": the five numbered components (callout +
                // fixed copy + KF Member Examples), then the KFOS closing block.
                <>
                  <PeerGroupComponents
                    componentContent={componentContent}
                    callouts={report.sections.component_callouts}
                    matchedExamples={report.sections.matched_examples}
                  />
                  <ClosingBlock
                    kfosClosing={kfosClosing}
                    closingSectionOrder={closingSectionOrder}
                    closingSections={report.sections.closing_sections}
                  />
                </>
              ) : reportShape === "nine_part_peer_group" ? (
                meetingParts.map(({ key, title }) => {
                  const body = report.sections.meeting_parts?.[key]
                  return body ? (
                    <ReportSection
                      key={key}
                      title={title}
                      section={{ body_markdown: body }}
                    />
                  ) : null
                })
              ) : null}
            </>
          ) : (
            sectionOrder.map(({ key, title }) => (
              <ReportSection key={key} title={title} section={report.sections[key]} />
            ))
          )}

          <RecommendedPrograms
            programs={report.sections?.recommended_programs}
            currentUser={currentUser}
          />

          {coach?.scheduleCallUrl && (
            <div className="mt-12 rounded-lg border border-kf-red/20 bg-kf-red/5 p-6 text-center">
              <p className="text-zinc-700 mb-4">
                Ready to talk this through together?
              </p>
              <a
                href={coach.scheduleCallUrl}
                className="inline-block bg-kf-red text-white rounded-md px-6 py-3 font-semibold hover:bg-kf-red/90"
              >
                Schedule a conversation with {coach.name}
              </a>
            </div>
          )}

          <footer className="mt-16 text-center text-xs text-zinc-400">
            Report generated{" "}
            {report.generatedAt
              ? new Date(report.generatedAt).toLocaleDateString()
              : "recently"}
            .
          </footer>
        </main>
      </div>
    </>
  )
}
